import DOMPurify from 'dompurify';

/**
 * Use-case bullets arrive as catalog copy that may already contain a link, and
 * most of the rest name a documentation URL in plain prose. Both have to render
 * as markup, so both go through the sanitizer.
 *
 * The allowlist is a link and inline emphasis, which is everything the catalog
 * actually uses: of the 479 use-case strings the workspace catalog serves today,
 * one carries an anchor and the rest are prose. Anything else is stripped to its
 * text rather than rendered.
 */
const ALLOWED_TAGS = ['a', 'b', 'strong', 'i', 'em', 'code', 'br'];
const ALLOWED_ATTR = ['href', 'target', 'rel'];

/**
 * A bare URL in prose becomes a link.
 *
 * Anchored on whitespace or a boundary, and applied to the **first** match only —
 * a bullet that names two URLs links the first and leaves the second as text.
 */
const BARE_URL = /(\s|^)(https?:\/\/[\w\-._~:/?#[\]@!$&'()*+,;=%]+)(\s|$)/;

/** Attribute characters that would break out of the `href` this builds. */
const UNSAFE_IN_ATTRIBUTE = /["'<>]/;

function linkFor(url: string): HTMLAnchorElement {
  const anchor = document.createElement('a');
  anchor.setAttribute('href', url);
  anchor.setAttribute('target', '_blank');
  anchor.setAttribute('rel', 'noopener noreferrer');
  anchor.textContent = url;
  return anchor;
}

/**
 * Replaces the first bare URL inside `root`'s text with a link, in place.
 *
 * Walking text nodes rather than rewriting the serialized HTML is what keeps a URL
 * that appears inside an attribute value from being turned into markup there —
 * which would produce a broken tag rather than a link.
 */
function linkifyFirstUrl(root: ParentNode): void {
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);

  for (let node = walker.nextNode(); node != null; node = walker.nextNode()) {
    // A URL already inside a link is left alone; nesting anchors is invalid.
    if (node.parentElement?.closest('a') != null) {
      continue;
    }

    const text = node.textContent ?? '';
    const match = BARE_URL.exec(text);

    if (match == null || UNSAFE_IN_ATTRIBUTE.test(match[2] ?? '')) {
      continue;
    }

    const [, lead = '', url = '', trail = ''] = match;
    const fragment = document.createDocumentFragment();

    fragment.append(text.slice(0, match.index) + lead, linkFor(url), trail + text.slice(match.index + match[0].length));
    node.parentNode?.replaceChild(fragment, node);
    return;
  }
}

/**
 * One use-case bullet as sanitized HTML, ready to render.
 *
 * Sanitizing before linkifying keeps the order that matters: a link the catalog
 * supplied is validated first, and only the surviving text can grow a new one.
 */
export function parseUseCase(useCase: string): string {
  const clean = DOMPurify.sanitize(useCase, { ALLOWED_TAGS, ALLOWED_ATTR });
  const holder = document.createElement('div');

  // Assigning sanitizer output, then reading it back through innerHTML.
  // nosemgrep: javascript.browser.security.insecure-document-method.insecure-document-method
  holder.innerHTML = clean;
  linkifyFirstUrl(holder);

  return holder.innerHTML;
}

/**
 * Every bullet worth rendering, parsed.
 *
 * A bullet whose whole content is markup outside the allowlist sanitizes down to
 * nothing; it is dropped rather than rendered as an empty list item.
 *
 * @see parseUseCase
 */
export function parseUseCases(useCases: readonly string[] | undefined): string[] {
  return (useCases ?? []).map(parseUseCase).filter((useCase) => useCase.trim() !== '');
}
